import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ShiftDataGrid = ({ 
  shifts, 
  selectedShifts, 
  onShiftSelect, 
  onShiftBook, 
  onBulkBook, 
  sortConfig, 
  onSort,
  onShiftDetails 
}) => {
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setSelectAll(selectedShifts?.length === shifts?.length && shifts?.length > 0);
  }, [selectedShifts, shifts]);

  const handleSelectAll = (checked) => {
    if (checked) {
      onShiftSelect(shifts?.map(shift => shift?.id));
    } else {
      onShiftSelect([]);
    }
  };

  const handleShiftSelection = (shiftId, checked) => {
    if (checked) {
      onShiftSelect([...selectedShifts, shiftId]);
    } else {
      onShiftSelect(selectedShifts?.filter(id => id !== shiftId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return "ArrowUpDown";
    return sortConfig?.direction === 'asc' ? "ArrowUp" : "ArrowDown";
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getDepartmentBadge = (department) => {
    const colors = {
      'customer-service': 'bg-blue-100 text-blue-800',
      'operations': 'bg-green-100 text-green-800',
      'manufacturing': 'bg-orange-100 text-orange-800',
      'administration': 'bg-purple-100 text-purple-800',
      'maintenance': 'bg-yellow-100 text-yellow-800',
      'security': 'bg-red-100 text-red-800'
    };

    const labels = {
      'customer-service': 'Kundendienst',
      'operations': 'Betrieb',
      'manufacturing': 'Fertigung',
      'administration': 'Verwaltung',
      'maintenance': 'Wartung',
      'security': 'Sicherheit'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors?.[department] || 'bg-gray-100 text-gray-800'}`}>
        {labels?.[department] || department}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return null;
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        <Icon name="Star" size={12} className="mr-1" />
        Priorität
      </span>
    );
  };

  const getCrossTrainingBadge = (hasCrossTraining) => {
    if (!hasCrossTraining) return null;
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
        <Icon name="GraduationCap" size={12} className="mr-1" />
        Weiterbildung
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header with bulk actions */}
      {selectedShifts?.length > 0 && (
        <div className="bg-primary/10 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {selectedShifts?.length} Schichten ausgewählt
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => onBulkBook(selectedShifts)}
                iconName="Calendar"
                iconPosition="left"
              >
                Alle buchen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onShiftSelect([])}
                iconName="X"
              >
                Auswahl aufheben
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Data Grid */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-12 p-3">
                <Checkbox
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('date')}
                  iconName={getSortIcon('date')}
                  iconPosition="right"
                  className="font-medium"
                >
                  Datum
                </Button>
              </th>
              <th className="text-left p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('startTime')}
                  iconName={getSortIcon('startTime')}
                  iconPosition="right"
                  className="font-medium"
                >
                  Zeit
                </Button>
              </th>
              <th className="text-left p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('department')}
                  iconName={getSortIcon('department')}
                  iconPosition="right"
                  className="font-medium"
                >
                  Abteilung
                </Button>
              </th>
              <th className="text-left p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('duration')}
                  iconName={getSortIcon('duration')}
                  iconPosition="right"
                  className="font-medium"
                >
                  Dauer
                </Button>
              </th>
              <th className="text-left p-3">Anforderungen</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {shifts?.map((shift, index) => (
              <tr 
                key={shift?.id}
                className={`border-b border-border hover:bg-muted/50 transition-colors ${
                  selectedShifts?.includes(shift?.id) ? 'bg-primary/5' : ''
                }`}
              >
                <td className="p-3">
                  <Checkbox
                    checked={selectedShifts?.includes(shift?.id)}
                    onChange={(e) => handleShiftSelection(shift?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-3">
                  <div className="font-medium text-foreground">
                    {formatDate(shift?.date)}
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-foreground">
                    {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
                  </div>
                </td>
                <td className="p-3">
                  {getDepartmentBadge(shift?.department)}
                </td>
                <td className="p-3">
                  <div className="text-foreground font-medium">
                    {shift?.duration}h
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {getPriorityBadge(shift?.isPriority)}
                    {getCrossTrainingBadge(shift?.hasCrossTraining)}
                    {shift?.requirements && (
                      <span className="text-xs text-muted-foreground">
                        {shift?.requirements}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-sm text-success font-medium">Verfügbar</span>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onShiftDetails(shift)}
                      iconName="Eye"
                      iconSize={16}
                    >
                      Details
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onShiftBook(shift?.id)}
                      iconName="Calendar"
                      iconSize={16}
                    >
                      Buchen
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {shifts?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Keine Schichten gefunden
          </h3>
          <p className="text-muted-foreground">
            Versuchen Sie, Ihre Filter anzupassen oder einen anderen Zeitraum zu wählen.
          </p>
        </div>
      )}
    </div>
  );
};

export default ShiftDataGrid;